import React, { useEffect, useState } from 'react';
import { Menu, X, Instagram, Facebook } from 'lucide-react';
import { IntegratedLogo } from './IntegratedLogo';
type NavLink = {
  name: string;
  href: string;
};
type NavGroup = {
  title: string;
  links: NavLink[];
};
const navGroups: NavGroup[] = [{
  title: 'Descubre',
  links: [{
    name: 'Inicio',
    href: '#inicio'
  }, {
    name: 'El Problema',
    href: '#problema'
  }, {
    name: 'Solución',
    href: '#solucion'
  }]
}, {
  title: 'Tratamientos',
  links: [{
    name: 'Servicios',
    href: '#servicios'
  }, {
    name: 'Testimonios',
    href: '#testimonios'
  }, {
    name: 'Academia',
    href: '#academia'
  }]
}, {
  title: 'Información',
  links: [{
    name: 'FAQ',
    href: '#faq'
  }, {
    name: 'Contacto',
    href: '#contacto'
  }]
}];
export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Prevent body scroll while the menu is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);
  return <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 py-2 ${isScrolled ? 'bg-beige-50/90 backdrop-blur-md shadow-sm' : 'bg-transparent'}`} data-id="element-271">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-272">
          <div className="flex justify-between items-center" data-id="element-273">
            {/* Logo */}
            <a href="#inicio" className="flex-shrink-0 z-50 relative" data-id="element-274">
              <IntegratedLogo size={isScrolled ? 60 : 72} data-id="element-275" />
            </a>

            {/* Right side actions */}
            <div className="flex items-center space-x-4 sm:space-x-6" data-id="element-276">
              {/* Social icons (hidden on small screens) */}
              <div className="hidden sm:flex items-center space-x-4" data-id="element-277">
                <a href="https://instagram.com/alisadostefania.becerra" target="_blank" rel="noopener noreferrer" className="metallic-gold-icon hover:opacity-80 transition-opacity" aria-label="Instagram" data-id="element-278">
                  <Instagram size={20} data-id="element-279" />
                </a>
                <a href="https://www.facebook.com/profile.php?id=100068319984463" target="_blank" rel="noopener noreferrer" className="metallic-gold-icon hover:opacity-80 transition-opacity" aria-label="Facebook" data-id="element-280">
                  <Facebook size={20} data-id="element-281" />
                </a>
              </div>

              {/* CTA (hidden on small screens) */}
              <a href="https://wa.me/13479909488?text=Hola%2C%20me%20gustar%C3%ADa%20agendar%20una%20cita%20%E2%9C%A8" target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex metallic-gold-button text-white px-6 py-2.5 rounded-full text-sm font-medium shadow-sm hover:shadow-md transition-all" data-id="element-282">
                Agenda tu cita
              </a>

              {/* Hamburger Menu Button (visible on all screen sizes) */}
              <button className="z-[10001] relative flex items-center gap-2 px-3 py-2 rounded-full border border-gold-300/70 bg-white/60 backdrop-blur-sm text-neutral-800 hover:border-gold-400 hover:bg-white transition-all" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Abrir menú" aria-expanded={isMenuOpen} data-id="element-283">
                {isMenuOpen ? <X size={22} data-id="element-284" /> : <Menu size={22} data-id="element-285" />}
                <span className="hidden sm:inline text-sm font-medium tracking-wide" data-id="element-286">
                  Menú
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Backdrop */}
      <div className={`fixed inset-0 z-[9999] bg-mocha-900/40 backdrop-blur-sm transition-opacity duration-300 ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setIsMenuOpen(false)} aria-hidden="true" data-id="element-287" />

      {/* Slide-in Menu Panel */}
      <aside className={`fixed top-0 right-0 z-[10000] h-full w-full sm:max-w-md bg-beige-50 shadow-2xl transition-transform duration-300 ease-in-out flex flex-col ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`} role="dialog" aria-modal="true" aria-label="Menú de navegación" data-id="element-288">
        {/* Panel header */}
        <div className="flex items-center justify-between px-6 sm:px-8 py-6 border-b border-beige-200" data-id="element-289">
          <IntegratedLogo size={56} data-id="element-290" />
          <button className="p-2 -mr-2 text-neutral-700 hover:text-gold-500 transition-colors" onClick={() => setIsMenuOpen(false)} aria-label="Cerrar menú" data-id="element-291">
            <X size={26} data-id="element-292" />
          </button>
        </div>

        {/* Organized navigation groups */}
        <nav className="flex-grow overflow-y-auto px-6 sm:px-8 py-8 space-y-8" data-id="element-293">
          {navGroups.map(group => <div key={group.title} data-id="element-294">
              <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-gold-600 mb-3" data-id="element-295">
                {group.title}
              </h3>
              <ul className="space-y-1" data-id="element-296">
                {group.links.map(link => <li key={link.name} data-id="element-297">
                    <a href={link.href} onClick={() => setIsMenuOpen(false)} className="block text-xl font-serif text-neutral-800 py-2 hover:text-gold-500 hover:translate-x-1 transition-all" data-id="element-298">
                      {link.name}
                    </a>
                  </li>)}
              </ul>
            </div>)}
        </nav>

        {/* Panel footer with CTA + socials */}
        <div className="px-6 sm:px-8 py-6 border-t border-beige-200 space-y-5" data-id="element-299">
          <a href="https://wa.me/13479909488?text=Hola%2C%20me%20gustar%C3%ADa%20agendar%20una%20cita%20%E2%9C%A8" target="_blank" rel="noopener noreferrer" onClick={() => setIsMenuOpen(false)} className="metallic-gold-button text-white text-center block py-4 rounded-full text-base font-medium shadow-md hover:shadow-lg transition-all" data-id="element-300">
            Agenda por WhatsApp
          </a>
          <div className="flex justify-center space-x-8" data-id="element-301">
            <a href="https://instagram.com/alisadostefania.becerra" target="_blank" rel="noopener noreferrer" className="metallic-gold-icon hover:opacity-80 transition-opacity" aria-label="Instagram" data-id="element-302">
              <Instagram size={26} data-id="element-303" />
            </a>
            <a href="https://www.facebook.com/profile.php?id=100068319984463" target="_blank" rel="noopener noreferrer" className="metallic-gold-icon hover:opacity-80 transition-opacity" aria-label="Facebook" data-id="element-304">
              <Facebook size={26} data-id="element-305" />
            </a>
          </div>
        </div>
      </aside>
    </>;
};